/**
 * DappList Component
 *
 * Displays a table of all registered minting dapps with actions.
 *
 * @module containers/AdminMintingDapps/components/DappList
 * @category Admin
 */

import React from 'react';

/**
 * DappList - Table component for displaying minting dapps.
 *
 * @param {Array} dapps - Array of dapp objects
 * @param {Function} onEdit - Callback when edit button clicked
 * @param {Function} onDelete - Callback when delete button clicked
 * @param {Function} onRefresh - Callback when refresh requested
 */
const DappList = ({ dapps, onEdit, onDelete, onRefresh }) => {
  const formatType = (type: string) => {
    return type === 'internal' ?
      <span className="badge bg-success">Internal</span> :
      <span className="badge bg-info">External</span>;
  };

  const formatChains = (chains?: number[]) => {
    if (!chains || chains.length === 0) return <span className="text-muted">Any</span>;
    return chains.map(c => (
      <span key={c} className="badge bg-secondary me-1">{c}</span>
    ));
  };

  const truncate = (str: string, len: number) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  const truncateUrl = (url?: string) => {
    if (!url) return '-';
    try {
      const u = new URL(url);
      return `${u.hostname}${u.pathname.substring(0, 20)}`;
    } catch {
      return truncate(url, 40);
    }
  };

  if (dapps.length === 0) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">No Minting Dapps Registered</h5>
          <p className="text-muted small mb-4">
            Register your first minting dapp to get started.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => onEdit(null)}
          >
            <i className="fas fa-plus me-2"></i>
            Add Dapp
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fas fa-list me-2 text-primary"></i>
          Registered Dapps ({dapps.length})
        </h5>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={onRefresh}
          title="Refresh list"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Name</th>
                <th style={{ width: '12%' }}>ID</th>
                <th style={{ width: '8%' }}>Type</th>
                <th style={{ width: '10%' }}>Chains</th>
                <th style={{ width: '20%' }}>URL / Component</th>
                <th style={{ width: '10%' }}>Author</th>
                <th style={{ width: '10%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dapps.map((dapp) => (
                <tr key={dapp.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="flex-shrink-0 me-3"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: dapp.type === 'internal' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(23, 162, 184, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: dapp.type === 'internal' ? '#28a745' : '#17a2b8',
                        }}
                      >
                        <i className={`fas fa-${dapp.type === 'internal' ? 'cube' : 'external-link-alt'}`}></i>
                      </div>
                      <div>
                        <strong>{dapp.name}</strong>
                        {dapp.description && (
                          <div className="small text-muted">
                            {truncate(dapp.description, 60)}
                          </div>
                        )}
                        {(() => {
                          let tagsArray: string[] = [];
                          if (Array.isArray(dapp.tags)) {
                            tagsArray = dapp.tags;
                          } else if (typeof dapp.tags === 'string') {
                            try {
                              const parsed = JSON.parse(dapp.tags);
                              if (Array.isArray(parsed)) tagsArray = parsed;
                            } catch (e) {
                              console.warn('Failed to parse tags JSON:', e);
                            }
                          }
                          return tagsArray.length > 0 && (
                            <div className="mt-1">
                              {tagsArray.slice(0, 3).map(tag => (
                                <span key={tag} className="badge bg-secondary small me-1">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <code className="small px-2 py-1 rounded">{dapp.id}</code>
                  </td>
                  <td>{formatType(dapp.type)}</td>
                  <td>{formatChains(dapp.config?.allowedChains)}</td>
                  <td>
                    {dapp.type === 'external' ? (
                      <div>
                        <a
                          href={dapp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {truncateUrl(dapp.url)}
                          <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </div>
                    ) : (
                      <span className="text-muted small">
                        {dapp.component ? 'Component defined' : 'No component'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="small">{dapp.author || 'Unknown'}</span>
                    {dapp.version && (
                      <div className="small text-muted">v{dapp.version}</div>
                    )}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => onEdit(dapp)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => onDelete(dapp)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DappList;
